"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const unique_selector_1 = __importDefault(require("unique-selector"));
const webVitals_1 = require("../constants/webVitals");
/**
 * Returns the given number rounded to two decimal places.
 * @private
 */
const round2Dec = val => Math.round(val * 100) / 100;
/**
 * Returns a list of simplified `LayoutShift` entries including their values and
 * HTML elements as unique CSS selector strings from the raw `LayoutShift`
 * entries used to compose the given CLS report.
 *
 * Each object in the returned list will contain the following properties:
 *
 * - `pctOfCLS` (number) – The percentage impact this layout shift had on the
 *     final CLS score, rounded to two decimal places. E.g., 3.75.
 * - `LSNodes` (string[]) – A list of unique CSS selector strings representing
 *     HTML elements involved in this layout shift. Will contain at least one
 *     and at most five strings. E.g., `['body > main > h1#foo', 'body > section
 *     > p#bar', ... ]`.
 * - `LSValue` (number) – The value of this layout shift multiplied by the
 *     internal CLS multiplier, rounded to two decimal places. E.g., 10.43.
 *
 * Return example:
 *
 *   ```js
 *   [
 *     {
 *       pctOfCLS: 5.27,
 *       LSNodes: [
 *         'body > main > h1#foo',
 *         'body > section > p#bar',
 *         'body > footer > a.button'
 *       ],
 *       LSValue: 15.63
 *     },
 *     // more objects here...
 *   ]
 *   ```
 *
 * @param {*} CLSReport - CLS metric object provided to the `onCLS()` callback
 * @param {LayoutShift[]} CLSReport.entries - List of raw `LayoutShift` entries
 *   that contributing to this CLS score
 * @param {number} CLSReport.value - The total CLS score in this report
 *
 * @returns {Object[]} - A list of simplified `LayoutShift` entries.
 *
 * @see
 * - https://web.dev/cls/#layout-shift-score
 * - https://github.com/WICG/layout-instability#performance-api
 * - https://github.com/WICG/layout-instability#Source-Attribution
 * - https://developer.mozilla.org/en-US/docs/Web/API/LayoutShift
 * - https://github.com/GoogleChrome/web-vitals/blob/6a51624ad49480fff99c2d4919fb0383f827882b/src/getCLS.ts
 * - https://github.com/GoogleChrome/web-vitals/blob/6a51624ad49480fff99c2d4919fb0383f827882b/src/types.ts#L17
 *
 * @todo Consider replacing `getUniqueSelector()` with `getSelector()` from
 *   web-vitals to be consistent and reduce package (b/c it's much smaller than
 *   `unique-selector`.) See
 *   https://github.com/GoogleChrome/web-vitals/blob/e4460bfff17e68448f69435187c1a8e7d38024e9/src/lib/getSelector.ts#L24
 */
const getLayoutShiftEntries = ({ entries: rawLSEntries = [], value: CLSValue, } = {}) => {
    // Return null if this CLS event is malformed
    if (!rawLSEntries.length || !CLSValue)
        return null;
    // Generate layout shift entries excluding any invalid raw LS entries or
    // valid raw LS entries with no HTML nodes
    const LSEntries = rawLSEntries
        .map(({ sources: LSAttribs = [], value: LSValue }) => {
        // Skip this LayoutShift entry if it's malformed or 0
        if (!LSAttribs.length || !LSValue)
            return null;
        return {
            // Impact this layout shift had on the final CLS score
            pctOfCLS: round2Dec((LSValue / CLSValue) * 100),
            // Uniqe CSS selector strings of the HTML element nodes involved
            // in this layout shift. Exclude nodes from which we can't make
            // unique CSS selector strings
            LSNodes: LSAttribs.map(shiftEntrySource => (shiftEntrySource === null || shiftEntrySource === void 0 ? void 0 : shiftEntrySource.node) &&
                (0, unique_selector_1.default)(shiftEntrySource.node)).filter(selector => typeof selector === 'string'),
            // Value of this layout shift
            LSValue: round2Dec(LSValue * webVitals_1.CLS_MULT),
        };
    })
        .filter(LSEntry => LSEntry === null || LSEntry === void 0 ? void 0 : LSEntry.LSNodes.length);
    // Return layout shift entries or null if there aren't any
    return LSEntries.length ? LSEntries : null;
};
exports.default = getLayoutShiftEntries;
